import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Popover,
    Tooltip,
    Typography,
    Button,
    makeStyles,
    Theme,
} from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
    pointerWithin,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { toast } from 'react-toastify'

import { LeasePetMarker } from '../../../../components'
import {
    Lease,
    LeaseStatus,
    Unit,
    getLeaseStatusString,
    leaseDisplayedPets,
} from '../../../../models'
import { toMMDDYYYY } from '../../../../helpers'
import {
    LeaseBoardUnit,
    activeOccupantsOnArea,
    boardUnitMatchesSearch,
    leaseCanTransfer,
    leaseTransferCreatesNewLease,
    compareLeasesForBed,
    unitIsInLocationSelection,
} from './leaseBoard'
import { LeaseMoveDialog } from './LeaseMoveDialog'

const useStyles = makeStyles((theme: Theme) => ({
    board: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        paddingBottom: theme.spacing(2),
        scrollbarWidth: 'auto',
        '&::-webkit-scrollbar': {
            width: 14,
            height: 14,
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#9e9e9e',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#eeeeee',
        },
    },
    group: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
    },
    groupHead: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minHeight: 44,
        boxSizing: 'border-box',
        padding: theme.spacing(0.75, 1.25),
        backgroundColor: theme.palette.grey[50],
        border: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'inherit',
        flexShrink: 0,
    },
    groupHeadClosed: {
        borderBottom: 'none',
    },
    groupLabel: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: '20px',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    groupCount: {
        marginLeft: 'auto',
        flexShrink: 0,
        fontSize: '0.75rem',
        lineHeight: '20px',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    placeBar: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        flexShrink: 0,
        padding: theme.spacing(1, 1.25),
        backgroundColor: '#e7f5f4',
        border: '1px solid #008C85',
        borderRadius: theme.spacing(1),
    },
    unitRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minHeight: 44,
        padding: theme.spacing(0.5, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    unitName: {
        width: 120,
        flexShrink: 0,
        fontWeight: 600,
        fontSize: '0.8125rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fillCount: {
        width: 48,
        flexShrink: 0,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontVariantNumeric: 'tabular-nums',
    },
    slots: {
        display: 'flex',
        gap: 6,
        flex: 1,
        minWidth: 0,
        flexWrap: 'wrap',
    },
    slot: {
        flex: '1 1 160px',
        maxWidth: 280,
        minWidth: 150,
        minHeight: 32,
        borderRadius: 6,
        border: `1px solid ${theme.palette.divider}`,
        padding: '2px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: '#fff',
        overflow: 'hidden',
    },
    slotEmpty: {
        borderStyle: 'dashed',
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
        color: '#b45309',
        fontSize: '0.6875rem',
        fontWeight: 700,
        justifyContent: 'center',
    },
    slotOver: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef2ff',
        borderStyle: 'solid',
    },
    slotTarget: {
        cursor: 'pointer',
        borderColor: '#008C85',
        borderStyle: 'solid',
        boxShadow: 'inset 0 0 0 1px #008C85',
    },
    slotCurrent: {
        cursor: 'default',
        opacity: 0.7,
    },
    tenant: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
        flex: 1,
        height: 28,
        borderRadius: 4,
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        padding: '0 2px 0 6px',
    },
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        flex: 1,
        cursor: 'grab',
    },
    dragHandleLocked: {
        cursor: 'default',
    },
    moveButton: {
        border: 'none',
        background: 'transparent',
        color: '#008C85',
        fontWeight: 700,
        fontSize: '0.6875rem',
        lineHeight: '16px',
        cursor: 'pointer',
        padding: '0 4px',
        flexShrink: 0,
        fontFamily: 'inherit',
    },
    tenantName: {
        fontSize: '0.75rem',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    pip: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    moreChip: {
        height: 20,
        fontSize: '0.625rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    overlay: {
        padding: '4px 10px',
        borderRadius: 6,
        backgroundColor: '#fff',
        border: `1px solid ${theme.palette.divider}`,
        fontWeight: 600,
        fontSize: '0.75rem',
        boxShadow: theme.shadows[3],
    },
    extraRow: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1, 1.5),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.grey[50],
        },
    },
}))

function statusPipColor(status: Lease['status']) {
    if (status === LeaseStatus.CURRENT) return '#047857'
    if (status === LeaseStatus.NOTICE) return '#c2410c'
    if (status === LeaseStatus.FUTURE) return '#1d4ed8'
    if (status === LeaseStatus.APPLICANT) return '#6b7280'
    if (status === LeaseStatus.CANCELLED) return '#b91c1c'
    return '#9ca3af'
}

const BoardActions = React.createContext<{
    onOpenLease: (lease: Lease) => void
    onStartPlace: (lease: Lease) => void
    placingLeaseId: number | null
    placingAreaId: number | null
} | null>(null)

function TenantChip({ lease }: { lease: Lease }) {
    const classes = useStyles()
    const actions = React.useContext(BoardActions)
    const canMove = leaseCanTransfer(lease)
    const placingThis = actions?.placingLeaseId === lease.id
    const petDisplay = leaseDisplayedPets(lease)
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: `lease-${lease.id}`,
        data: { leaseId: lease.id },
        disabled: !canMove || placingThis,
    })

    const stop = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    return (
        <div
            ref={setNodeRef}
            className={classes.tenant}
            style={{
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.45 : 1,
            }}
        >
            <div
                className={`${classes.dragHandle} ${
                    canMove && !placingThis ? '' : classes.dragHandleLocked
                }`}
                {...(canMove && !placingThis ? listeners : {})}
                {...(canMove && !placingThis ? attributes : {})}
            >
                <span
                    className={classes.pip}
                    style={{ backgroundColor: statusPipColor(lease.status) }}
                />
                <span
                    className={classes.tenantName}
                    onClick={(event) => {
                        event.stopPropagation()
                        actions?.onOpenLease(lease)
                    }}
                >
                    {lease.user.name}
                </span>
                <LeasePetMarker
                    pets={petDisplay.pets}
                    hasPet={petDisplay.hasPet}
                    style={{ width: 14, height: 14 }}
                />
            </div>
            {canMove && (
                <button
                    type="button"
                    className={classes.moveButton}
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onClick={(event) => {
                        stop(event)
                        actions?.onStartPlace(lease)
                    }}
                >
                    {placingThis ? 'Moving' : 'Move'}
                </button>
            )}
        </div>
    )
}

function BedSlot({
    areaId,
    label,
    leases,
    onOpenLease,
    onPlace,
}: {
    areaId: number
    label: string
    leases: Lease[]
    onOpenLease: (lease: Lease) => void
    onPlace: (areaId: number) => void
}) {
    const classes = useStyles()
    const actions = React.useContext(BoardActions)
    const { setNodeRef, isOver } = useDroppable({
        id: `area-${areaId}`,
        data: { areaId },
    })
    const [extraAnchor, setExtraAnchor] = useState<HTMLElement | null>(null)
    const primary = leases[0]
    const rest = leases.slice(1)
    const empty = !primary
    const placing = actions?.placingLeaseId != null
    const isCurrent = placing && actions?.placingAreaId === areaId
    const canPlace = placing && !isCurrent

    return (
        <div
            ref={setNodeRef}
            className={`${classes.slot} ${empty ? classes.slotEmpty : ''} ${
                isOver ? classes.slotOver : ''
            } ${canPlace ? classes.slotTarget : ''} ${
                isCurrent ? classes.slotCurrent : ''
            }`}
            onClick={
                canPlace
                    ? () => {
                          onPlace(areaId)
                      }
                    : undefined
            }
        >
            {empty ? (
                <span>{canPlace ? 'Click to place' : `${label} · Open`}</span>
            ) : (
                <>
                    <TenantChip lease={primary} />
                    {rest.length > 0 && (
                        <Chip
                            className={classes.moreChip}
                            size="small"
                            label={`+${rest.length}`}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation()
                                setExtraAnchor(event.currentTarget)
                            }}
                        />
                    )}
                    <Popover
                        open={Boolean(extraAnchor)}
                        anchorEl={extraAnchor}
                        onClose={() => setExtraAnchor(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        {rest.map((lease) => {
                            const petDisplay = leaseDisplayedPets(lease)
                            return (
                                <div
                                    key={lease.id}
                                    className={classes.extraRow}
                                >
                                    <div
                                        onClick={() => {
                                            setExtraAnchor(null)
                                            onOpenLease(lease)
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                            }}
                                        >
                                            {lease.user.name}
                                            <LeasePetMarker
                                                pets={petDisplay.pets}
                                                hasPet={petDisplay.hasPet}
                                                style={{
                                                    width: 14,
                                                    height: 14,
                                                }}
                                            />
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {getLeaseStatusString(lease.status)}{' '}
                                            ·{' '}
                                            {toMMDDYYYY(
                                                new Date(lease.start_date),
                                            )}{' '}
                                            –{' '}
                                            {toMMDDYYYY(
                                                new Date(lease.end_date),
                                            )}
                                        </Typography>
                                    </div>
                                    {leaseCanTransfer(lease) && (
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                setExtraAnchor(null)
                                                actions?.onStartPlace(lease)
                                            }}
                                            style={{
                                                textTransform: 'none',
                                                alignSelf: 'flex-start',
                                                paddingLeft: 0,
                                            }}
                                        >
                                            Move
                                        </Button>
                                    )}
                                </div>
                            )
                        })}
                    </Popover>
                </>
            )}
        </div>
    )
}

export function LeaseBedBoard({
    units,
    filteredLeases,
    allLeases,
    searchValue,
    locationUnits,
    busy,
    onOpenLease,
    onTransfer,
}: {
    units: LeaseBoardUnit[]
    filteredLeases: Lease[]
    allLeases: Lease[]
    searchValue: string
    locationUnits: { [id: number]: Unit; length: number }
    busy: boolean
    onOpenLease: (lease: Lease) => void
    onTransfer: (lease: Lease, areaId: number) => Promise<void>
}) {
    const classes = useStyles()
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
    const [activeLeaseId, setActiveLeaseId] = useState<number | null>(null)
    const [placingLease, setPlacingLease] = useState<Lease | null>(null)
    const [moveLease, setMoveLease] = useState<Lease | null>(null)
    const [pending, setPending] = useState<{
        lease: Lease
        areaId: number
        messages: string[]
        closeMoveDialog: boolean
    } | null>(null)
    const inFlight = useRef(false)

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    )

    const filteredByArea = useMemo(() => {
        const map = new Map<number, Lease[]>()
        for (const lease of filteredLeases) {
            const areaId = lease.area?.id
            if (areaId == null) continue
            const list = map.get(areaId) ?? []
            list.push(lease)
            map.set(areaId, list)
        }
        map.forEach((list) => list.sort(compareLeasesForBed))
        return map
    }, [filteredLeases])

    const visibleUnits = useMemo(() => {
        return units.filter((unit) => {
            if (!unitIsInLocationSelection(locationUnits, unit.id)) {
                return false
            }
            const occupantText = unit.areas
                .flatMap((area) => filteredByArea.get(area.id) ?? [])
                .map((lease) => `${lease.user.name} ${lease.user.email}`)
                .join(' ')
            return boardUnitMatchesSearch(unit, occupantText, searchValue)
        })
    }, [units, locationUnits, filteredByArea, searchValue])

    const grouped = useMemo(() => {
        const byKey = new Map<
            string,
            { key: string; label: string; units: LeaseBoardUnit[] }
        >()
        for (const unit of visibleUnits) {
            const label = unit.unitConfigName || 'Unit type'
            const key = unit.unitConfigId ? String(unit.unitConfigId) : label
            const existing = byKey.get(key)
            if (existing) {
                existing.units.push(unit)
            } else {
                byKey.set(key, { key, label, units: [unit] })
            }
        }
        const groups = Array.from(byKey.values())
        groups.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true }),
        )
        for (const group of groups) {
            group.units.sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
        }
        return groups
    }, [visibleUnits])

    const leaseById = useMemo(() => {
        const map = new Map<number, Lease>()
        for (const lease of allLeases) map.set(lease.id, lease)
        return map
    }, [allLeases])

    const activeLease =
        activeLeaseId == null ? null : leaseById.get(activeLeaseId) ?? null

    const runTransfer = (
        lease: Lease,
        areaId: number,
        closeMoveDialog: boolean,
    ) => {
        if (inFlight.current || busy) return
        inFlight.current = true
        onTransfer(lease, areaId)
            .then(() => {
                setPending(null)
                setPlacingLease(null)
                if (closeMoveDialog) setMoveLease(null)
            })
            .catch(() => undefined)
            .finally(() => {
                inFlight.current = false
            })
    }

    const requestMove = (
        lease: Lease,
        areaId: number,
        closeMoveDialog: boolean,
    ) => {
        if (busy || inFlight.current) return
        if (!leaseCanTransfer(lease)) {
            toast.error('This lease cannot be moved.')
            return
        }
        if (lease.area?.id === areaId) return

        const messages: string[] = []
        if (leaseTransferCreatesNewLease(lease)) {
            messages.push(
                'This lease will be ended and a new lease will be started on the selected bed.',
            )
        }
        const occupants = activeOccupantsOnArea(allLeases, areaId, lease.id)
        if (occupants.length > 0) {
            const names = occupants.map((row) => row.user.name).join(', ')
            messages.push(
                `${names} ${
                    occupants.length === 1 ? 'is' : 'are'
                } already on this bed and will stay there. Only ${
                    lease.user.name
                } will be moved.`,
            )
        }
        if (messages.length === 0) {
            runTransfer(lease, areaId, closeMoveDialog)
            return
        }
        setPending({ lease, areaId, messages, closeMoveDialog })
    }

    const confirmPending = () => {
        if (!pending) return
        runTransfer(pending.lease, pending.areaId, pending.closeMoveDialog)
    }

    const handleDragStart = (event: DragStartEvent) => {
        const leaseId = event.active.data.current?.leaseId as number | undefined
        setActiveLeaseId(leaseId ?? null)
    }

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveLeaseId(null)
        const leaseId = event.active.data.current?.leaseId as number | undefined
        const areaId = event.over?.data.current?.areaId as number | undefined
        if (leaseId == null || areaId == null) return
        const lease = leaseById.get(leaseId)
        if (!lease) return
        requestMove(lease, areaId, false)
    }

    useEffect(() => {
        if (!placingLease) return
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setPlacingLease(null)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [placingLease])

    const placeOnArea = (areaId: number) => {
        if (!placingLease) return
        requestMove(placingLease, areaId, false)
    }

    return (
        <BoardActions.Provider
            value={{
                onOpenLease,
                onStartPlace: (lease) => setPlacingLease(lease),
                placingLeaseId: placingLease?.id ?? null,
                placingAreaId: placingLease?.area?.id ?? null,
            }}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveLeaseId(null)}
            >
                <div className={classes.board}>
                    {placingLease && (
                        <div className={classes.placeBar}>
                            <Typography variant="body2" style={{ flex: 1 }}>
                                Moving <b>{placingLease.user.name}</b>. Click a
                                bed to place them.
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setMoveLease(placingLease)}
                                style={{ textTransform: 'none' }}
                            >
                                Search beds
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setPlacingLease(null)}
                                style={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                    {grouped.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No units match this filter.
                        </Typography>
                    ) : (
                        grouped.map((group) => {
                            const isCollapsed = collapsed[group.key] === true
                            return (
                                <div key={group.key} className={classes.group}>
                                    <button
                                        type="button"
                                        className={`${classes.groupHead} ${
                                            isCollapsed
                                                ? classes.groupHeadClosed
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setCollapsed((current) => ({
                                                ...current,
                                                [group.key]: !current[
                                                    group.key
                                                ],
                                            }))
                                        }
                                    >
                                        {isCollapsed ? (
                                            <KeyboardArrowRightIcon
                                                fontSize="small"
                                                style={{ flexShrink: 0 }}
                                            />
                                        ) : (
                                            <KeyboardArrowDownIcon
                                                fontSize="small"
                                                style={{ flexShrink: 0 }}
                                            />
                                        )}
                                        <span className={classes.groupLabel}>
                                            {group.label}
                                        </span>
                                        <span className={classes.groupCount}>
                                            {group.units.length}{' '}
                                            {group.units.length === 1
                                                ? 'unit'
                                                : 'units'}
                                        </span>
                                    </button>
                                    {!isCollapsed &&
                                        group.units.map((unit) => {
                                            const filled = unit.areas.filter(
                                                (area) =>
                                                    (filteredByArea.get(area.id)
                                                        ?.length ?? 0) > 0,
                                            ).length
                                            return (
                                                <div
                                                    key={unit.id}
                                                    className={classes.unitRow}
                                                >
                                                    <Tooltip
                                                        title={
                                                            unit.folderLabel ||
                                                            unit.name
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                classes.unitName
                                                            }
                                                        >
                                                            {unit.name}
                                                        </span>
                                                    </Tooltip>
                                                    <span
                                                        className={
                                                            classes.fillCount
                                                        }
                                                    >
                                                        {filled}/
                                                        {unit.areas.length}
                                                    </span>
                                                    <div
                                                        className={
                                                            classes.slots
                                                        }
                                                    >
                                                        {unit.areas.map(
                                                            (area) => (
                                                                <BedSlot
                                                                    key={
                                                                        area.id
                                                                    }
                                                                    areaId={
                                                                        area.id
                                                                    }
                                                                    label={`${area.areaConfigName} ${area.areaLabel}`}
                                                                    leases={
                                                                        filteredByArea.get(
                                                                            area.id,
                                                                        ) ?? []
                                                                    }
                                                                    onOpenLease={
                                                                        onOpenLease
                                                                    }
                                                                    onPlace={
                                                                        placeOnArea
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                </div>
                            )
                        })
                    )}
                </div>
                <DragOverlay>
                    {activeLease ? (
                        <div className={classes.overlay}>
                            {activeLease.user.name}
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>

            <LeaseMoveDialog
                lease={moveLease}
                units={units}
                leases={allLeases}
                open={moveLease != null}
                busy={busy}
                onClose={() => setMoveLease(null)}
                onPick={(areaId) => {
                    if (!moveLease) return
                    requestMove(moveLease, areaId, true)
                }}
            />

            <Dialog
                open={pending != null}
                onClose={() => {
                    if (!busy) setPending(null)
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Move lease</DialogTitle>
                <DialogContent>
                    {pending?.messages.map((message) => (
                        <Typography
                            key={message}
                            variant="body2"
                            style={{ marginBottom: 8 }}
                        >
                            {message}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={confirmPending}
                        disabled={busy}
                    >
                        Move lease
                    </Button>
                </DialogActions>
            </Dialog>
        </BoardActions.Provider>
    )
}
